import React from "react";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ className, children, ...rest }) {
  return (
    <div className={cn("bg-white border border-slate-200 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)]", className)} {...rest}>
      {children}
    </div>
  );
}

const ACCENTS = {
  blue: "bg-blue-50 text-blue-600",
  green: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  red: "bg-red-50 text-red-600",
  violet: "bg-violet-50 text-violet-600",
  sky: "bg-sky-50 text-sky-600",
};

export function StatCard({ label, value, sub, icon: Icon, accent = "blue", trend, delay = 0 }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] p-5 transition-all duration-200 hover:shadow-[0_12px_32px_-12px_rgba(15,23,42,0.22)] hover:-translate-y-0.5 animate-fade-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500 font-medium">{label}</p>
        {Icon && <div className={cn("p-2 rounded-lg", ACCENTS[accent])}><Icon size={20} weight="duotone" /></div>}
      </div>
      <p className="text-3xl font-heading font-extrabold mt-3 tabular text-slate-900">{value}</p>
      {sub && <p className={cn("text-xs mt-1 font-medium", trend === "up" ? "text-emerald-600" : trend === "down" ? "text-red-500" : "text-slate-400")}>{sub}</p>}
    </div>
  );
}

const STATUS_STYLES = {
  new: "bg-sky-50 text-sky-700 ring-sky-200",
  contacted: "bg-amber-50 text-amber-700 ring-amber-200",
  converted: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  lost: "bg-red-50 text-red-700 ring-red-200",
  pending: "bg-amber-50 text-amber-700 ring-amber-200",
  active: "bg-blue-50 text-blue-700 ring-blue-200",
  completed: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  open: "bg-slate-100 text-slate-700 ring-slate-200",
  in_progress: "bg-blue-50 text-blue-700 ring-blue-200",
  done: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  unpaid: "bg-red-50 text-red-700 ring-red-200",
  low: "bg-slate-100 text-slate-700 ring-slate-200",
  medium: "bg-amber-50 text-amber-700 ring-amber-200",
  high: "bg-red-50 text-red-700 ring-red-200",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  active_: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  inactive: "bg-slate-100 text-slate-600 ring-slate-200",
  maintenance: "bg-amber-50 text-amber-700 ring-amber-200",
  available: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  in_use: "bg-blue-50 text-blue-700 ring-blue-200",
  expired: "bg-red-50 text-red-700 ring-red-200",
  valid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

export function StatusBadge({ status, children, className }) {
  const raw = String(status || "");
  const key = raw.toLowerCase();
  let style = STATUS_STYLES[key];
  if (!style) {
    if (key.includes("reject") || key.includes("risk") || key.includes("lost")) style = "bg-red-50 text-red-700 ring-red-200";
    else if (key.includes("approv") || key.includes("ready") || key.includes("final") || key.includes("convert")) style = "bg-emerald-50 text-emerald-700 ring-emerald-200";
    else if (key.includes("awaiting") || key.includes("pending")) style = "bg-amber-50 text-amber-700 ring-amber-200";
    else if (key.includes("compliance") || key.includes("sd team") || key.includes("assigned")) style = "bg-sky-50 text-sky-700 ring-sky-200";
    else style = "bg-slate-100 text-slate-700 ring-slate-200";
  }
  return (
    <span className={cn("inline-flex items-center capitalize px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset", style, className)}>
      {children || raw.replace(/_/g, " ")}
    </span>
  );
}

export function EmptyState({ title, hint, icon: Icon, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && <div className="p-3 rounded-2xl bg-slate-50 mb-4"><Icon size={36} weight="duotone" className="text-slate-300" /></div>}
      <p className="text-slate-700 font-semibold">{title}</p>
      {hint && <p className="text-sm text-slate-400 mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-600">{label}</label>
      {children}
    </div>
  );
}

export function money(n) {
  return "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
